import React, { useState } from "react";
import "@/App.css";
import {
  BrowserRouter,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { Toaster } from "sonner";
import AuthCallback from "./components/AuthCallback";
import IOSAuthBridge from "./components/IOSAuthBridge";
import PaymentReturn from "./components/PaymentReturn";
import Paywall from "./components/Paywall";
import AboutPage from "./components/legal/AboutPage";
import TermsPage from "./components/legal/TermsPage";
import PrivacyPage from "./components/legal/PrivacyPage";
import SupportPage from "./components/legal/SupportPage";
import PaywallIOSPreview from "./components/paywall/PaywallIOSPreview";
import Home from "./components/v2/Home";
import SavedTab from "./components/v2/SavedTab";
import RecentsTab from "./components/v2/RecentsTab";
import SettingsTab from "./components/v2/SettingsTab";
import TripsTab from "./components/v2/TripsTab";
import TravelVaultTab from "./components/v2/TravelVaultTab";
import TSARulesPage from "./components/v2/TSARulesPage";
import LostLuggagePage from "./components/v2/LostLuggagePage";
import BottomNav from "./components/v2/BottomNav";
import BackendOutageBanner from "./components/v2/BackendOutageBanner";
import DeleteConfirmDialog from "./components/DeleteConfirmDialog";
import SplitGuestPage from "./pages/SplitGuestPage";
import { AuthProvider, useAuth } from "./lib/auth";
import { PaywallProvider, usePaywall } from "./lib/paywall";

function AppShell() {
  const [tab, setTab] = useState("home");
  // Home sub-view is owned here (lifted from Home.jsx) so Settings can
  // offer a one-tap "Back to Translate" pill when the user dipped into
  // Settings from a Translate context. Values: "tip" | "currency" |
  // "translate" | "translate:menu" | "phrases".
  const [homeSubView, setHomeSubView] = useState("tip");
  // Pending one-shot action triggered when navigating across tabs (e.g.
  // SavedTab "Scan receipt" → Home tab with autoScanReceipt=true).
  const [pendingAction, setPendingAction] = useState(null);
  const { open, reason, openPaywall, closePaywall } = usePaywall();

  // Navigate helper — accepts "tab" or "tab:action" forms.
  const navigate = (target) => {
    if (typeof target === "string" && target.includes(":")) {
      const [t, action] = target.split(":");
      setTab(t);
      setPendingAction(action);
    } else {
      // Tap-on-same-tab semantics: tapping "Home" while already inside a
      // Home sub-view (Currency / Translate / Phrasebook) should drop the
      // user back to the Tip screen instead of doing nothing.
      if (target === "home" && tab === "home") {
        setPendingAction("reset-home");
      } else {
        setPendingAction(null);
      }
      setTab(target);
    }
  };

  const consumeAction = () => setPendingAction(null);

  return (
    <div className="App min-h-screen bg-bone relative">
      <Toaster position="top-center" richColors duration={2500} closeButton />
      <DeleteConfirmDialog />
      <BackendOutageBanner />

      <div className="max-w-md mx-auto min-h-screen bg-transparent pb-safe-tabbar relative z-10">
        <main className="animate-fade-up" key={tab}>
          {tab === "home" && (
            <Home
              openPaywall={() => openPaywall()}
              onNavigate={navigate}
              pendingAction={pendingAction}
              consumeAction={consumeAction}
              view={homeSubView}
              setView={setHomeSubView}
            />
          )}
          {tab === "saved" && (
            <SavedTab openPaywall={() => openPaywall()} onNavigate={navigate} />
          )}
          {tab === "vault" && (
            <TravelVaultTab
              openPaywall={() => openPaywall()}
              onBack={() => navigate("home")}
              pendingAction={pendingAction}
              consumeAction={consumeAction}
            />
          )}
          {tab === "tsa" && (
            <TSARulesPage openPaywall={() => openPaywall()} onBack={() => navigate("home")} />
          )}
          {tab === "lost" && (
            <LostLuggagePage onBack={() => navigate("home")} />
          )}
          {tab === "recents" && (
            <RecentsTab openPaywall={() => openPaywall()} />
          )}
          {tab === "settings" && (
            <SettingsTab
              openPaywall={() => openPaywall()}
              returnToTranslate={
                ["translate", "translate:menu", "phrases"].includes(homeSubView)
                  ? () => {
                      setHomeSubView("translate");
                      setTab("home");
                    }
                  : null
              }
            />
          )}
          {tab === "trips" && (
            <TripsTab onBack={() => navigate("home")} />
          )}
        </main>
      </div>

      <BottomNav tab={tab} onChange={(t) => navigate(t)} />

      <Paywall open={open} reason={reason} onClose={closePaywall} />
    </div>
  );
}

function AuthedShell() {
  const { refresh } = useAuth();
  const location = useLocation();
  // OAuth callback returns to "/" with #session_id=... (web only — iOS is
  // handled directly by the deep-link listener in AuthProvider).
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback onAuthed={refresh} />;
  }
  return <AppShell />;
}

function App() {
  return (
    <BrowserRouter>
      <PaywallProvider>
        <AuthProvider>
          <Routes>
            <Route path="/ios-auth-bridge" element={<IOSAuthBridge />} />
            <Route path="/pay/return" element={<PaymentReturn />} />
            <Route path="/pay/cancel" element={<Navigate to="/" replace />} />
            {/* Public, no-auth-required legal/marketing pages. Stripe’s
                review team must be able to reach these from a logged-out
                session, so they sit OUTSIDE AuthedShell. */}
            <Route path="/about" element={<AboutPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/split/:code" element={<SplitGuestPage />} />
            {/* Dev-only marketing preview of the iOS paywall (Build 38).
                Not linked from anywhere — used to produce the App Store
                Connect "Subscription Review Information → Screenshot"
                image. Safe to leave in production; renders mock data only. */}
            <Route path="/dev/paywall-preview" element={<PaywallIOSPreview />} />
            <Route path="/*" element={<AuthedShell />} />
          </Routes>
        </AuthProvider>
      </PaywallProvider>
    </BrowserRouter>
  );
}

export default App;
